<template>
  <div class="grid grid-cols-1 md:grid-cols-4 gap-6 w-full">
    <!-- Card 1 - Nível de Maturidade -->
    <div
      class="bg-gradient-to-br from-gray-700 to-blue-700 text-white p-6 rounded-2xl shadow-lg"
    >
      <div class="flex items-center mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-2 text-white"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M2 11a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1z" />
        </svg>
        <h2 class="text-sm font-semibold">Nível de Maturidade</h2>
      </div>
      <p class="text-4xl font-bold">{{ animatedNivelMaturidade }}%</p>
      <p class="text-xs mt-2">Escala baseada na implementação</p>
    </div>

    <!-- Card 2 - Controles Implementados -->
    <div class="bg-white p-6 rounded-2xl shadow-lg text-center">
      <div class="flex justify-center mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2 class="text-sm font-semibold text-gray-600">
        Controles Implementados
      </h2>
      <p class="text-3xl font-bold text-green-600">
        {{ percentual(animatedControleImplementado) }}%
      </p>
      <p class="text-xs text-gray-500 mt-2">
        Total de controles: {{ animatedControleImplementado }}
      </p>
    </div>

    <!-- Card 3 - Controles Parciais -->
    <div class="bg-white p-6 rounded-2xl shadow-lg text-center">
      <div class="flex justify-center mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-yellow-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h2 class="text-sm font-semibold text-gray-600">Controles Parciais</h2>
      <p class="text-3xl font-bold text-yellow-500">
        {{ percentual(animatedControleParcial) }}%
      </p>
      <p class="text-xs text-gray-500 mt-2">
        Total de controles: {{ animatedControleParcial }}
      </p>
    </div>

    <!-- Card 4 - Não Implementados -->
    <div class="bg-white p-6 rounded-2xl shadow-lg text-center">
      <div class="flex justify-center mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>
      <h2 class="text-sm font-semibold text-gray-600">Não Implementados</h2>
      <p class="text-3xl font-bold text-red-600">
        {{ percentual(animatedControleNaoImplementado) }}%
      </p>
      <p class="text-xs text-gray-500 mt-2">
        Total de controles: {{ animatedControleNaoImplementado }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Card',
  props: {
    totalControles: {
      type: Number,
      default: 93,
    },
    controleImplementado: {
      type: Number,
      default: 0,
    },
    controleParcial: {
      type: Number,
      default: 0,
    },
    controleNaoImplementado: {
      type: Number,
      default: 0,
    },
    controleNaoAplicavel: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      animatedControleImplementado: 0,
      animatedControleParcial: 0,
      animatedControleNaoImplementado: 0,
      animatedNivelMaturidade: 0,
      animatedNaoAplicavel: 0,
    }
  },
  computed: {
    nivelMaturidade() {
      const I = this.controleImplementado ?? 0
      const P = this.controleParcial ?? 0
      const N = this.controleNaoImplementado ?? 0

      const totalConsiderado = I + P + N
      if (totalConsiderado === 0) return 0

      const maturidade = ((I + 0.5 * P) / totalConsiderado) * 100
      return Math.round(maturidade)
    },
  },

  watch: {
    controleImplementado(newVal) {
      this.animateCount('animatedControleImplementado', newVal)
      this.animateCount('animatedNivelMaturidade', this.nivelMaturidade)
    },
    controleParcial(newVal) {
      this.animateCount('animatedControleParcial', newVal)
    },
    controleNaoImplementado(newVal) {
      this.animateCount('animatedControleNaoImplementado', newVal)
      this.animateCount('animatedNivelMaturidade', this.nivelMaturidade)
    },
    controleNaoAplicavel(newVal) {
      this.animateCount('animatedNaoAplicavel', newVal)
    },
  },
  mounted() {
    this.animateCount('animatedControleImplementado', this.controleImplementado)
    this.animateCount('animatedControleParcial', this.controleParcial)
    this.animateCount(
      'animatedControleNaoImplementado',
      this.controleNaoImplementado
    )
    this.animateCount('animatedNivelMaturidade', this.nivelMaturidade)
    this.animateCount('animatedNaoAplicavel', this.controleNaoAplicavel)
  },
  methods: {
    animateCount(variableName, targetValue) {
      if (typeof targetValue !== 'number' || isNaN(targetValue)) return

      const duration = 800
      const start = this[variableName] || 0
      const startTime = performance.now()

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const current = Math.round(start + (targetValue - start) * progress)
        this[variableName] = current

        if (progress < 1) {
          requestAnimationFrame(animate)
        }
      }

      requestAnimationFrame(animate)
    },
    percentual(value) {
      if (!this.totalControles || this.totalControles === 0) return 0
      return Math.min(
        Math.max(Math.round((value / this.totalControles) * 100), 0),
        100
      )
    },
  },
}
</script>
